import React, { Fragment, useState, useEffect } from "react";
import { gql, useQuery } from "urql";
import { Link } from "wouter";
import { groupBy, keyBy, round } from "lodash";

import { Grid, Column } from "../../../../components/src";
import { TooltipLabel } from "../../../../components/src/TooltipLabel";

import {
    InstrumentPosition,
    Instrument,
    Party,
    CashAccountLadder,
    InstrumentModelTypeEnum,
    InstrumentModelLegTypesEnum,
    IssuerProgramCategory,
    TransactionStatus
} from "../../types.generated";
import { DateHelper } from "../../../../common/src";
import { numberFormatFun } from "../../../../common/src/utils/FormatFunctions";
import { getCdsExposureRatio } from "./cdsExposure";
import { addOrdersToAccountBalances, NettedOrderValue, nettedOrderValues } from "./cashLadderOrders";

const getParties = gql`
    query getParties {
        parties(filter: { typeIn: [Fund, Mandate], statusIn: [Confirmed] }) {
            _id
            name
        }
    }
`;

const getData = gql`
    query getData($clientIds: [GraphQLObjectId!]!, $endDate: GraphQLDateString) {
        allInstrumentsInPosition {
            clients {
                name
            }
            instrument {
                _id
                name
                modelType
                maturityDate
                model {
                    legs {
                        _t
                        cashFlows {
                            exDate
                            payDate
                        }
                    }
                }
            }
        }
        cashLadder(endDate: $endDate, clientIds: $clientIds) {
            client {
                _id
                name
            }
            instrumentId
            instrument {
                name
                currency
                productType
            }
            dates
            balances
        }
        tradingmanager(filterZeroPositions: true, filter: { endDate: $endDate, clientIds: $clientIds }, lookThrough: true) {
            clientId
            exposure
            quantity
            fxRate
            maturityDate
            swaplab2Results(date: $endDate) {
                interestRateYieldDelta
                creditYieldDelta
            }
            modelType
            instrument {
                model {
                    legs {
                        notional
                    }
                }
                issuerProgram {
                    category
                }
            }
        }
    }
`;

const getTodaysOrders = gql`
    query getDashboardOrders($date: GraphQLDateString) {
        transferAgentOrders(filter: { tradeDate: $date }) {
            _id
            status
            transactionType
            currency
            amount
            units
            instrument {
                _id
                name
            }
            clientId
            valueDate
        }
        parties(filter: { typeIn: [Fund] }) {
            _id
            fundInfo {
                classes {
                    startNAVPerUnit
                    instrumentId
                }
            }
            accounts {
                defaultAccount
                defaultCashInstruments {
                    currency
                    partyInstrumentId
                }
            }
        }
        navs(date: $date, lastOnly: true) {
            clientId
            data {
                classes {
                    instrumentId
                    name
                    navCurrency
                    navPerUnit
                }
            }
            date
        }
    }
`;

const moneyFormat = numberFormatFun("# ##0,00");

interface InstrumentExtended extends Instrument {
    positionClients?: string;
    exDate?: string;
}

const sortInstruments = (allInstruments: InstrumentPosition[], showAll: boolean, endDate: string) => {
    const instrumentsFiltered: InstrumentExtended[] = [];
    // Only choose up to max_days days into the future from end date
    const max_days = 90;
    const future_date = DateHelper.dateAddDays(new Date(endDate), max_days);

    allInstruments.forEach((item) => {
        if (item.instrument.maturityDate !== null) {
            const instr_new: InstrumentExtended = item.instrument;
            instr_new.positionClients = item.clients
                .map(function (e) {
                    return e.name;
                })
                .join(", ");

            // Extract exDate from FixedAmountLeg, or use maturityDate if FixedAmountLeg doesn't exist
            let exDate: string | null = null;
            if (item.instrument.model?.legs) {
                // Find FixedAmountLeg
                const fixedAmountLeg = item.instrument.model.legs.find((leg) => leg._t === InstrumentModelLegTypesEnum.FixedAmountLeg);
                if (fixedAmountLeg?.cashFlows && fixedAmountLeg.cashFlows.length > 0) {
                    // Get exDate from the last item in the cashFlows array
                    const lastCashFlow = fixedAmountLeg.cashFlows[fixedAmountLeg.cashFlows.length - 1];
                    if (lastCashFlow?.exDate) {
                        exDate = lastCashFlow.exDate;
                    }
                }
            }

            // If no exDate found from FixedAmountLeg, use maturityDate
            instr_new.exDate = exDate || item.instrument.maturityDate || undefined;

            if (showAll === true) {
                instrumentsFiltered.push(instr_new);
            } else if (showAll === false && DateHelper.toDate(item.instrument.maturityDate) < future_date) {
                instrumentsFiltered.push(instr_new);
            }
        }
    });
    instrumentsFiltered.sort((a, b) => a.exDate.localeCompare(b.exDate));

    return instrumentsFiltered;
};

interface CashOverview {
    client: Party;
    instrumentId?: string;
    name: string;
    date: string;
    today: number;
    tomorrow: number;
    twoDays: number;
    threeDays: number;
}

// Today, tomorrow, 2 days and 3 days, skipping weekends
const getBalanceDates = (endDate: string): string[] => {
    const currentDate: Date = new Date(endDate);
    let futureDate1: Date;
    let futureDate2: Date;
    let futureDate3: Date;

    futureDate1 = DateHelper.dateAddDays(currentDate, 1);
    if (futureDate1.getDay() == 6) {
        futureDate1 = DateHelper.dateAddDays(new Date(futureDate1), 2);
    } else if (futureDate1.getDay() == 0) {
        futureDate1 = DateHelper.dateAddDays(new Date(futureDate1), 1);
    }

    if (futureDate1.getDay() == 5) {
        futureDate2 = DateHelper.dateAddDays(new Date(futureDate1), 3);
    } else {
        futureDate2 = DateHelper.dateAddDays(new Date(futureDate1), 1);
    }

    if (futureDate2.getDay() == 5) {
        futureDate3 = DateHelper.dateAddDays(new Date(futureDate2), 3);
    } else {
        futureDate3 = DateHelper.dateAddDays(new Date(futureDate2), 1);
    }

    return [currentDate, futureDate1, futureDate2, futureDate3].map((d) => d.toISOString().slice(0, 10));
};

const getTodaysBalances = (cashLadder: CashAccountLadder[], endDate: string) => {
    const result: CashOverview[] = [];
    const [today, add1, add2, add3] = getBalanceDates(endDate);

    cashLadder.forEach((item) => {
        if (item.instrument.productType === "CashAccount") {
            const idx_tdy = item.dates.indexOf(today);
            const idx_add1 = item.dates.indexOf(add1);
            const idx_add2 = item.dates.indexOf(add2);
            const idx_add3 = item.dates.indexOf(add3);

            if (!(idx_tdy === -1 && idx_add1 === -1 && idx_add2 === -1 && idx_add3 === -1)) {
                result.push({
                    client: item.client,
                    instrumentId: item.instrumentId,
                    name: item.instrument.name,
                    date: item.dates[idx_tdy],
                    today: item.balances[idx_tdy],
                    tomorrow: item.balances[idx_add1],
                    twoDays: item.balances[idx_add2],
                    threeDays: item.balances[idx_add3]
                });
            }
        }
    });
    return result;
};

const getRiskNumbers = (clients, trades) => {
    const positionsByClientId = groupBy(trades, (i) => i.clientId);

    const result = clients.map((fund) => {
        let duration;
        let creditDuration;
        let creditDurationBonds;
        let esgBondsPercent;
        let cdsExposure;

        const positions = positionsByClientId[fund._id];
        if (positions) {
            const portfolioNav = positions.reduce((acc, o) => acc + o.exposure, 0);
            cdsExposure = getCdsExposureRatio(positions, portfolioNav);
            const sumInterestRateYieldDelta = positions.reduce((acc, o) => acc + (o.swaplab2Results?.interestRateYieldDelta ?? 0), 0);
            duration = (sumInterestRateYieldDelta / portfolioNav) * 10000;

            const sumCreditYieldDelta = positions.reduce((acc, o) => acc + (o.swaplab2Results?.creditYieldDelta ?? 0), 0);
            creditDuration = (sumCreditYieldDelta / portfolioNav) * 10000;

            const sumBondsCreditYieldDelta: number = positions
                .filter((x) => x.modelType == InstrumentModelTypeEnum.Bond)
                .reduce((acc, o) => acc + (o.swaplab2Results?.creditYieldDelta ?? 0), 0);

            const sumBondsExposure: number = positions
                .filter((x) => x.modelType == InstrumentModelTypeEnum.Bond)
                .reduce((acc, o) => acc + o.exposure, 0);

            const sumBondsWithCategoryExposure: number = positions
                .filter(
                    (x) =>
                        x.modelType == InstrumentModelTypeEnum.Bond &&
                        x.instrument.issuerProgram &&
                        x.instrument.issuerProgram.category !== IssuerProgramCategory.None
                )
                .reduce((acc, o) => acc + o.exposure, 0);

            esgBondsPercent = round((sumBondsWithCategoryExposure / sumBondsExposure) * 100, 1);
            creditDurationBonds = (sumBondsCreditYieldDelta / sumBondsExposure) * 10000;
        }

        return {
            client: {
                _id: fund._id,
                name: fund.name
            },
            duration,
            creditDuration,
            creditDurationBonds,
            cdsExposure,
            esgBondsPercent
        };
    });

    return result;
};

const Checkbox = (props) => <input type="checkbox" {...props} />;

export function Dashboard({ endDate }: { endDate: string }): React.ReactElement {
    const [checkedShowAll, setCheckedShowAll] = useState(false);
    const [checkedIncludeOrders, setCheckedIncludeOrders] = useState(false);
    const [clientList, setClientList] = useState(null);
    const [trades, setTrades] = useState([]);

    const [{ fetching: loadingParties, data: dataParties }] = useQuery({ query: getParties, requestPolicy: "network-only" });

    // TODO: Query for allInstrumentsInPosition does not support date in argument
    const [{ fetching: loading, error: error, data: data }] = useQuery({
        query: getData,
        pause: clientList === null,
        variables: { clientIds: clientList ? clientList.map((p: Party) => p._id) : [], endDate },
        requestPolicy: "network-only"
    });

    const ordersTradeDate = endDate;

    const [{ fetching: fetchingOrders, error: errorOrders, data: dataOrders }] = useQuery({
        query: getTodaysOrders,
        variables: { date: ordersTradeDate },
        requestPolicy: "network-only",
        pause: !checkedIncludeOrders
    });

    const [instrumentData, setinstrumentData] = useState(null);
    const [cashLadder, setCashLadder] = useState(null);

    useEffect(() => {
        if (dataParties && dataParties.parties) {
            const clients = dataParties.parties.map((p: Party) => {
                return { _id: p._id, name: p.name };
            });
            setClientList(clients);
        } else {
            setClientList(null);
        }
    }, [dataParties]);

    useEffect(() => {
        if (data) {
            setinstrumentData(data.allInstrumentsInPosition);
            setCashLadder(data.cashLadder);
            setTrades(data.tradingmanager);
        }
    }, [dataParties, data]);

    if (error) return <p>error: {JSON.stringify(error, null, 2)}</p>;

    if (loadingParties) return <p>Loading parties...</p>;
    if (loading) return <div>Loading...</div>;

    if (!instrumentData) return <div>"No data found"</div>;

    const handleCheckboxChangeShowAll = (e) => {
        setCheckedShowAll(e.target.checked);
    };

    const handleCheckboxChangeIncludeOrders = (e) => {
        setCheckedIncludeOrders(e.target.checked);
    };

    const instruments: InstrumentExtended[] = sortInstruments(instrumentData, checkedShowAll, endDate);
    let cashBalances: CashOverview[] = getTodaysBalances(cashLadder, endDate);
    const riskNumbers = getRiskNumbers(clientList, trades);

    const ordersLoaded = checkedIncludeOrders && !fetchingOrders && !errorOrders && dataOrders;
    let fundsWithOrders = 0;
    let fundsWithSettledOrders = 0;
    if (ordersLoaded) {
        const nettedOrdersPerClientId: Record<string, NettedOrderValue[]> = {};
        (dataOrders.parties ?? []).forEach((fund: Party) => {
            const netted = nettedOrderValues(fund, dataOrders.transferAgentOrders ?? [], dataOrders.navs ?? []);
            if (netted.length > 0) {
                nettedOrdersPerClientId[fund._id] = netted;
            } else if (
                (dataOrders.transferAgentOrders ?? []).some((o) => o.clientId === fund._id && o.status === TransactionStatus.Settled)
            ) {
                fundsWithSettledOrders++;
            }
        });
        fundsWithOrders = Object.keys(nettedOrdersPerClientId).length;

        const defaultCashInstrumentId: Record<string, string> = {};
        (dataOrders.parties ?? []).forEach((fund: Party) => {
            const mainAccount = fund.accounts?.find((account) => account.defaultAccount);
            mainAccount?.defaultCashInstruments?.forEach((cashInstrument) => {
                defaultCashInstrumentId[fund._id + ":" + cashInstrument.currency] = cashInstrument.partyInstrumentId;
            });
        });

        const dates = getBalanceDates(endDate);
        const clientsById = keyBy([...clientList, ...cashLadder.map((item: CashAccountLadder) => item.client)], "_id");
        const cashAccountLadders = cashLadder.filter((item: CashAccountLadder) => item.instrument.productType === "CashAccount");
        const cashAccounts = cashAccountLadders.map((item: CashAccountLadder) => ({
            clientId: item.client._id,
            instrumentId: item.instrumentId,
            name: item.instrument.name,
            currency: item.instrument.currency,
            dates: item.dates,
            balances: item.balances,
            isDefaultCash: defaultCashInstrumentId[item.client._id + ":" + item.instrument.currency] === item.instrumentId
        }));
        cashBalances = [...cashBalances];
        addOrdersToAccountBalances(cashAccounts, nettedOrdersPerClientId, dates).forEach((account) => {
            const accountRow: CashOverview = {
                client: (clientsById[account.clientId] ?? { _id: account.clientId, name: "" }) as Party,
                instrumentId: account.instrumentId,
                name: `Netted orders ${account.currency}`,
                date: dates[0],
                today: account.balances[0],
                tomorrow: account.balances[1],
                twoDays: account.balances[2],
                threeDays: account.balances[3]
            };
            const ladder = cashAccountLadders.find(
                (item: CashAccountLadder) => item.instrumentId === account.instrumentId && item.client._id === account.clientId
            );
            if (ladder) {
                accountRow.client = ladder.client;
                accountRow.name = ladder.instrument.name;
            }

            const existingRow = cashBalances.findIndex(
                (r) => account.instrumentId && r.instrumentId === account.instrumentId && r.client._id === account.clientId
            );
            if (existingRow !== -1) {
                cashBalances[existingRow] = accountRow;
                return;
            }
            if (account.balances.every((b) => typeof b === "undefined")) {
                return;
            }
            const lastFundRow = cashBalances.findLastIndex((r) => r.client._id === account.clientId);
            if (lastFundRow === -1) {
                cashBalances.push(accountRow);
            } else {
                cashBalances.splice(lastFundRow + 1, 0, accountRow);
            }
        });
    }

    let ordersStatus = "Loading orders...";
    if (errorOrders) {
        ordersStatus = `Could not load orders for ${ordersTradeDate}: ${errorOrders.message}`;
    } else if (ordersLoaded && fundsWithOrders + fundsWithSettledOrders > 0) {
        const parts: string[] = [];
        if (fundsWithOrders > 0) {
            parts.push(`for ${fundsWithOrders} ${fundsWithOrders === 1 ? "fund" : "funds"} included`);
        }
        if (fundsWithSettledOrders > 0) {
            parts.push(`for ${fundsWithSettledOrders} ${fundsWithSettledOrders === 1 ? "fund" : "funds"} already in the balance`);
        }
        ordersStatus = `(orders on ${ordersTradeDate} ${parts.join(", ")})`;
    } else if (ordersLoaded) {
        ordersStatus = `(no orders on ${ordersTradeDate})`;
    }

    return (
        <Fragment>
            <div className="flex flex-wrap -mx-2 mb-2">
                <div className="w-full px-2">
                    <label>
                        <Checkbox checked={checkedShowAll} onChange={handleCheckboxChangeShowAll} />
                        <span> Show all expiring instruments </span>
                    </label>
                    <label className="ml-4">
                        <Checkbox checked={checkedIncludeOrders} onChange={handleCheckboxChangeIncludeOrders} />
                        <span>
                            {" "}
                            Include{" "}
                            <a href="https://portal.captor.se/transferagentorders" target="_blank" rel="noreferrer">
                                transfer agent fund orders
                            </a>{" "}
                            (if the client selected is a fund){" "}
                        </span>
                    </label>
                    {checkedIncludeOrders ? <span className="ml-2 text-gray-600">{ordersStatus}</span> : null}
                </div>
            </div>
            <div className="flex flex-wrap -mx-2">
                <div className="w-full px-2 xl:w-5/12">
                    <Grid
                        header="Cash balances"
                        data={cashBalances}
                        sortable
                        tableClassName="table-xs [&_td+td]:pl-5! [&_th+th]:pl-5!"
                        negativeClassName="warning"
                    >
                        <Column
                            field="client.name"
                            className="nowrap left"
                            title="Client"
                            format={(_, item) => {
                                return (
                                    <Link to={"/pm/" + item.client._id + "/cashLadder"}>{item && item.client ? item.client.name : ""}</Link>
                                );
                            }}
                        />
                        <Column field="name" className="nowrap left" />
                        <Column field="today" className="nowrap right" format={numberFormatFun("# ##0")} />
                        <Column field="tomorrow" className="nowrap right" format={numberFormatFun("# ##0")} />
                        <Column field="twoDays" title="2 days" className="nowrap right" format={numberFormatFun("# ##0")} />
                        <Column field="threeDays" title="3 days" className="nowrap right" format={numberFormatFun("# ##0")} />
                    </Grid>

                    <Grid header="Risk numbers" data={riskNumbers} sortable tableClassName="table-xs" negativeClassName="warning">
                        <Column
                            field="client.name"
                            className="nowrap left"
                            title="Client"
                            format={(_, item) => {
                                return (
                                    <Link to={"/pm/" + item.client._id + "/cashLadder"}>{item && item.client ? item.client.name : ""}</Link>
                                );
                            }}
                        />
                        <Column
                            field="duration"
                            title="Duration"
                            format={(value) => {
                                return value !== undefined ? moneyFormat(value) : "n/a";
                            }}
                            className="nowrap right"
                        />
                        <Column
                            field="creditDuration"
                            title="Credit duration"
                            format={(value) => {
                                return value !== undefined ? moneyFormat(value) : "n/a";
                            }}
                            className="nowrap right"
                        />
                        <Column
                            field="creditDurationBonds"
                            title="Credit duration bonds"
                            format={(value) => {
                                return value !== undefined ? moneyFormat(value) : "n/a";
                            }}
                            className="nowrap right"
                        />
                        <Column
                            field="cdsExposure"
                            title="CDS exposure"
                            format={(value) => {
                                return value !== undefined ? numberFormatFun("# ##0.00%")(value) : "-";
                            }}
                            className="nowrap right"
                        />
                        <Column
                            field="esgBondsPercent"
                            title={<TooltipLabel text={"ESG Bonds %"} content={"Value of ESG bonds / total bond value"} />}
                            className="nowrap center"
                            format={numberFormatFun("0.0")}
                        />
                    </Grid>
                </div>
                <div className="w-full px-2 xl:w-7/12">
                    <Grid header="Upcoming expiries" data={instruments} sortable tableClassName="table-xs" negativeClassName="warning">
                        <Column field="exDate" title="Ex date" className="nowrap left" />
                        <Column field="modelType" title="Model type" />
                        <Column
                            field="_id"
                            className="nowrap left"
                            title="Instrument"
                            format={(_, item) => {
                                return <Link to={"/instruments/" + item._id}>{item ? item.name : ""}</Link>;
                            }}
                        />
                        <Column field="maturityDate" title="Maturity date" className="nowrap left" />
                        <Column field="positionClients" title="Clients" />
                    </Grid>
                </div>
            </div>
        </Fragment>
    );
}
